import { SimilarFilesResponse } from "@/api";
import { FilesDropZone } from "@/components/FilesDropZone/FilesDropZone";
import DocumentFilesToUpload from "@/components/FilesToUpload/DocumentFilesToUpload";
import { ImageFilesToUpload } from "@/components/FilesToUpload/ImageFilesToUpload";
import { VideoFilesToUpload } from "@/components/FilesToUpload/VideoFilesToUpload";
import { NavigationButtons } from "@/components/NavigationButtons/NavigationButtons";
import { BelongsToMarketPrompt } from "@/components/Preferences/BelongsToMarketPrompt";
import { ModalBodyWithSimilarImages } from "@/components/SimilarImages/ModalBodyWithSimilarImages";
import { PRIVACY_GUIDELINES_TEXT } from "@/constants";
import { useSimilarAssets } from "@/hooks/assets";
import { useFilesUpload } from "@/hooks/filesUpload";
import { LocalFile } from "@/hooks/types";
import Modal, { Sheets } from "@ingka/modal";
import Text from "@ingka/text";
import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";

export const FilesSelectorPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    localFiles,
    uploadProgress,
    setOnFilesSelected,
    removeFile,
    createUploadAndAssets,
    isVerifyingFiles,
    anyUploading,
    retryUpload,
    canCreateUpload,
  } = useFilesUpload();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState("");
  const [isCreatingUpload, setIsCreatingUpload] = useState(false);
  const {
    loadingSimilarAssets,
    similarAssets,
    setSimilarAssets,
    checkForSimilarAssets,
  } = useSimilarAssets();

  const [showPreferencePrompt, setShowPreferencePrompt] = useState(false);
  const [nextPagePath, setNextPagePath] = useState("");

  const uploadType = location.state?.uploadType;
  if (!uploadType) {
    throw new Error("Expected upload type missing");
  }

  const onCreateUploadAssets = async () => {
    if (!canCreateUpload) return;

    setIsCreatingUpload(true);
    try {
      const { createdUpload } = await createUploadAndAssets(uploadType);
      const path = `/uploads/${createdUpload.id}/confirm-files`;
      setNextPagePath(path);

      const isPreferenceSet = createdUpload?.user.preference?.belongsToMarket;
      if (isPreferenceSet === undefined || isPreferenceSet === -1) {
        setShowPreferencePrompt(true);
        return;
      } else {
        navigate(path);
      }
    } finally {
      setIsCreatingUpload(false);
    }
  };

  const localFilesMap = useMemo(() => {
    return localFiles.reduce(
      (acc, file) => {
        acc[file.file.name] = file;
        return acc;
      },
      {} as Record<string, LocalFile>,
    );
  }, [localFiles]);

  const failedFileUploads = localFiles.filter(
    (f) => f.uploadStatus === "failed",
  );
  const pendingSuccessfulFileUploads = localFiles.filter(
    (file) => !failedFileUploads.includes(file),
  );

  const renderFileSection = (localFiles: LocalFile[]) => (
    <>
      {uploadType === "IMAGE" && (
        <ImageFilesToUpload
          isLoadingSimilarAssets={loadingSimilarAssets}
          localFiles={localFiles}
          similarFiles={similarAssets}
          uploadProgress={uploadProgress}
          removeFile={(localFile: LocalFile) => {
            removeFile(localFile.url);

            // Removing the removed file from similarFiles
            const { [localFile.file.name]: _, ...updatedFiles } = similarAssets;
            setSimilarAssets(updatedFiles as SimilarFilesResponse);
          }}
          retryUpload={retryUpload}
          setSelectedFile={(filename) => {
            setSelectedFile(filename);
            setModalOpen(true);
          }}
        />
      )}
      {uploadType === "VIDEO" && (
        <VideoFilesToUpload
          localFiles={localFiles}
          uploadProgress={uploadProgress}
          removeFile={(localFile: LocalFile) => {
            removeFile(localFile.url);

            // Removing the removed file from similarFiles
            const { [localFile.file.name]: _, ...updatedFiles } = similarAssets;
            setSimilarAssets(updatedFiles as SimilarFilesResponse);
          }}
          retryUpload={retryUpload}
        />
      )}
      {uploadType === "DOCUMENT" && (
        <DocumentFilesToUpload
          localFiles={localFiles}
          uploadProgress={uploadProgress}
          removeFile={(localFile: LocalFile) => removeFile(localFile.url)}
          retryUpload={retryUpload}
        />
      )}
    </>
  );

  return (
    <div className="flex flex-col">
      {showPreferencePrompt && (
        <BelongsToMarketPrompt
          onSuccess={() => {
            setShowPreferencePrompt(false);
            navigate(nextPagePath);
          }}
        />
      )}
      <div className="mb-space300 self-center">
        <FilesDropZone
          uploadType={uploadType}
          onFilesSelected={async (files: File[]) => {
            setOnFilesSelected(files);
            if (uploadType === "IMAGE") {
              checkForSimilarAssets(files);
            }
          }}
        />
      </div>
      <section className="mx-space400 flex flex-col">
        {failedFileUploads.length > 0 && (
          <div className="my-space100">
            <Text tagName="h2" headingSize="xs" className="mb-space100">
              File(s) failed to upload
            </Text>
            <Text tagName="p" className="my-space100">
              We encountered an issue processing your files. Click the retry
              icon to try uploading again.
            </Text>
            {renderFileSection(failedFileUploads)}
          </div>
        )}
        {pendingSuccessfulFileUploads.length > 0 && (
          <div className="my-space100">
            <Text tagName="h2" headingSize="xs" className="mb-space100">
              File(s) added to your request
            </Text>
            {renderFileSection(pendingSuccessfulFileUploads)}
          </div>
        )}
      </section>

      <div
        className={
          localFiles.length === 0
            ? "mt-space200 w-[26rem] self-center text-center"
            : "mx-space400 mt-space150 w-[35rem]"
        }
      >
        <Text tagName="p" bodySize="s">
          {PRIVACY_GUIDELINES_TEXT}
        </Text>
      </div>
      <div className="my-space300 ml-auto">
        <NavigationButtons
          onPrev={() => navigate("/")}
          onContinue={onCreateUploadAssets}
          isPrevLoading={loadingSimilarAssets || anyUploading}
          isNextLoading={
            isCreatingUpload ||
            isVerifyingFiles ||
            anyUploading ||
            loadingSimilarAssets
          }
          disabledNext={!canCreateUpload || isCreatingUpload}
          nextButtonTitle="Save draft & continue"
        />
      </div>
      {selectedFile && (
        <Modal
          visible={modalOpen}
          escapable={true}
          handleCloseBtn={() => setModalOpen(false)}
        >
          <Sheets
            header={null}
            footer={null}
            size="small"
            aria-label={`Similar images for ${selectedFile}`}
          >
            {localFilesMap[selectedFile] && (
              <ModalBodyWithSimilarImages
                fileToUploadUrl={localFilesMap[selectedFile].url}
                filename={localFilesMap[selectedFile].file.name}
                similarFiles={similarAssets[selectedFile].result}
              />
            )}
          </Sheets>
        </Modal>
      )}
    </div>
  );
};
